import type { LeadSource } from '../../../types/leads';

/**
 * A lead can be enriched on more than one network, and it used to show only one.
 *
 * `Lead.sourceUrl` is a single column, and the enrichment orchestrator only
 * overwrites it when the scraped profile lands on the PRIMARY contact. Enrich
 * LinkedIn and then Instagram and the Instagram URL goes to a new non-primary
 * contact, leaving the lead pointing at LinkedIn — so the badge, the pipeline
 * card and the list all claimed a single source.
 *
 * Three signals are read, in order of reliability:
 *   - `contacts[].sourceUrl` — the actual per-profile URLs, but the kanban board
 *     payload does not include contacts, so this is often absent.
 *   - `sourceUrl` — always present.
 *   - `tags` — written per platform by the enrichment, on the lead row itself, so
 *     they survive into every payload including the board. User-editable, which is
 *     why they are a signal rather than the only one.
 */

interface PlatformSource {
  source?: LeadSource | string | null;
  sourceUrl?: string | null;
  tags?: string[] | null;
  contacts?: Array<{ sourceUrl?: string | null }> | null;
}

/** Only the networks a profile URL can identify. */
const URL_PLATFORMS = [
  { platform: 'linkedin', host: 'linkedin.com' },
  { platform: 'instagram', host: 'instagram.com' },
  { platform: 'facebook', host: 'facebook.com' },
  { platform: 'twitter', host: 'twitter.com' },
] as const;

export type LeadPlatform = (typeof URL_PLATFORMS)[number]['platform'];

const PLATFORM_ORDER: readonly LeadPlatform[] = URL_PLATFORMS.map((entry) => entry.platform);

function platformFromUrl(url: unknown): LeadPlatform | undefined {
  if (typeof url !== 'string') return undefined;
  const lower = url.toLowerCase();
  return URL_PLATFORMS.find((entry) => lower.includes(entry.host))?.platform;
}

function platformFromTag(tag: unknown): LeadPlatform | undefined {
  if (typeof tag !== 'string') return undefined;
  const normalized = tag.trim().toLowerCase();
  return PLATFORM_ORDER.find((platform) => platform === normalized);
}

/**
 * Every network this lead has been enriched on, in a stable order so the badges
 * never reshuffle between renders. Empty when nothing identifies a network — the
 * caller then falls back to the lead's own `source`.
 */
export function getLeadPlatforms(lead: PlatformSource): LeadPlatform[] {
  const found = new Set<LeadPlatform>();

  const fromLeadUrl = platformFromUrl(lead.sourceUrl);
  if (fromLeadUrl) found.add(fromLeadUrl);

  for (const contact of lead.contacts ?? []) {
    const fromContact = platformFromUrl(contact?.sourceUrl);
    if (fromContact) found.add(fromContact);
  }

  for (const tag of lead.tags ?? []) {
    const fromTag = platformFromTag(tag);
    if (fromTag) found.add(fromTag);
  }

  const fromSource = platformFromTag(lead.source);
  if (fromSource) found.add(fromSource);

  return PLATFORM_ORDER.filter((platform) => found.has(platform));
}

/**
 * The profile URLs to offer, one per network. Prefers the lead's own URL for its
 * platform so the link a user already knows does not move, then adds each
 * contact URL for a network the lead level does not cover.
 */
export function getLeadProfileUrls(
  lead: PlatformSource,
): Array<{ platform: LeadPlatform; url: string }> {
  const byPlatform = new Map<LeadPlatform, string>();

  const leadPlatform = platformFromUrl(lead.sourceUrl);
  if (leadPlatform && typeof lead.sourceUrl === 'string') {
    byPlatform.set(leadPlatform, lead.sourceUrl);
  }

  for (const contact of lead.contacts ?? []) {
    const platform = platformFromUrl(contact?.sourceUrl);
    if (platform && !byPlatform.has(platform) && typeof contact?.sourceUrl === 'string') {
      byPlatform.set(platform, contact.sourceUrl);
    }
  }

  return PLATFORM_ORDER.filter((platform) => byPlatform.has(platform)).map((platform) => ({
    platform,
    url: byPlatform.get(platform) as string,
  }));
}
